/**
 * Компонент для динамической генерации формы на основе JSON структуры
 */

import React, { FC } from 'react';
import { Form, Input, InputNumber, Switch, Card, Button, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { detectFieldType, isReadOnlyField } from './utils';
import CommentEditor from './CommentEditor';

interface IDynamicFormProps {
  data: any;
  prefix?: string[];
  comments?: { [key: string]: string };
  onRemoveField?: (fieldPath: string[]) => void;
  onAddFieldToObject?: (parentPath: string[]) => void;
  onCommentChange?: (fieldPath: string, comment: string) => void;
}

const DynamicForm: FC<IDynamicFormProps> = ({
  data,
  prefix = [],
  comments = {},
  onRemoveField,
  onAddFieldToObject,
  onCommentChange
}) => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const renderField = (key: string, value: any) => {
    const fieldPath = [...prefix, key];
    const fieldName = fieldPath.join('.');
    const fieldType = detectFieldType(value);
    const readOnly = isReadOnlyField(key);

    // Если значение - вложенный объект, рендерим карточку с вложенными полями
    if (fieldType === 'object') {
      return (
        <Card
          key={fieldName}
          title={key}
          size="small"
          style={{ marginBottom: 16 }}
          extra={
            !readOnly &&
            onRemoveField && (
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => onRemoveField(fieldPath)}
              />
            )
          }
        >
          <DynamicForm
            data={value}
            prefix={fieldPath}
            comments={comments}
            onRemoveField={onRemoveField}
            onAddFieldToObject={onAddFieldToObject}
            onCommentChange={onCommentChange}
          />
          {onAddFieldToObject && (
            <Button
              type="dashed"
              onClick={() => onAddFieldToObject(fieldPath)}
              block
              icon={<PlusOutlined />}
              style={{ marginTop: 16 }}
            >
              Добавить поле в {key}
            </Button>
          )}
        </Card>
      );
    }

    // Рендерим кнопку удаления
    const renderDeleteButton = () => {
      if (!readOnly && onRemoveField) {
        return (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => onRemoveField(fieldPath)}
            style={{ marginLeft: 8 }}
          />
        );
      }
      return null;
    };

    const fieldComment = comments[fieldName];
    const labelContent = (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {key}
        {fieldComment && (
          <Tooltip title={fieldComment}>
            <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 14 }} />
          </Tooltip>
        )}
      </span>
    );

    return (
      <div key={fieldName} style={{ marginBottom: 24 }}>
        <Form.Item
          name={fieldName}
          label={labelContent}
          initialValue={value}
          valuePropName={fieldType === 'boolean' ? 'checked' : 'value'}
          style={{
            marginBottom: 0,
            display: 'inline-block',
            width: readOnly || !onRemoveField ? '100%' : 'calc(100% - 80px)'
          }}
        >
          {fieldType === 'boolean' ? (
            <Switch disabled={readOnly} />
          ) : fieldType === 'number' ? (
            <InputNumber style={{ width: '100%' }} disabled={readOnly} />
          ) : (
            <Input
              disabled={readOnly}
              placeholder={fieldType === 'date' ? 'ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ)' : ''}
            />
          )}
        </Form.Item>
        <span style={{ marginLeft: 8 }}>
          {onCommentChange && (
            <CommentEditor
              fieldPath={fieldName}
              currentComment={fieldComment || ''}
              onSave={onCommentChange}
            />
          )}
          {renderDeleteButton()}
        </span>
      </div>
    );
  };

  return (
    <>
      {Object.keys(data).map(key => {
        return renderField(key, data[key]);
      })}
    </>
  );
};

export default DynamicForm;
